import { Stack } from '@mui/material';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from 'components/v2/common/pageHeader';
import AdminPanelFilters from 'views/v2/adminPanel/components/adminPanelFilters';
import ServiceProviderCountriesDialog from 'views/v2/adminPanel/components/serviceProviderCountriesDialog';
import ServiceProviderDefaultConsultantsDialog from 'views/v2/adminPanel/components/serviceProviderDefaultConsultantsDialog';
import ServiceProviderFeaturesDialog from 'views/v2/adminPanel/components/serviceProviderFeaturesDialog';
import ServiceProvidersTable, {
    type ServiceProviderAction,
} from 'views/v2/adminPanel/components/serviceProvidersTable';
import SupportEmailForm from 'views/v2/adminPanel/components/supportEmailForm';

import api from 'api';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { RootState } from 'redux/store';
import { setEnabledCountryCodes } from 'redux/user/user-slice';
import type {
    CountryCode,
    ServiceProviderAdminRow,
    ServiceProviderConfigUpdatePayload,
    SupportEmailFormInterface,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface ServiceProvidersPanelProps {
    headerControls?: ReactNode;
}

export default function ServiceProvidersPanel({ headerControls }: ServiceProvidersPanelProps) {
    const dispatch = useDispatch();
    const currentServiceProviderId = useSelector(
        (state: RootState) => state.user.entity.service_provider_id
    );
    const { translate } = useTranslate();
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<ServiceProviderAdminRow>();

    const [providers, setProviders] = useState<ServiceProviderAdminRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const response = await api.serviceProvider.list();
                if (!cancelled) setProviders(response.data);
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(error, translate('somethingWentWrong')),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, translate]);

    const companyOptions = useMemo(() => {
        const names = providers.map((provider) => provider.company_name).filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }, [providers]);

    const filteredProviders = useMemo(() => {
        const query = search.trim().toLowerCase();
        return providers.filter((provider) => {
            if (selectedCompany && provider.company_name !== selectedCompany) return false;
            if (!query) return true;
            return (
                provider.company_name.toLowerCase().includes(query) ||
                (provider.support_email ?? '').toLowerCase().includes(query)
            );
        });
    }, [providers, search, selectedCompany]);

    const handleAction = (action: ServiceProviderAction, provider: ServiceProviderAdminRow) => {
        switch (action) {
            case 'COUNTRIES':
            case 'DEFAULT_CONSULTANTS':
            case 'FEATURES':
            case ACTIONS.EDIT:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data: provider,
                    loading: false,
                    disabled: false,
                }));
                break;
            default:
                break;
        }
    };

    const handleSupportEmailSubmit = async (values: SupportEmailFormInterface) => {
        const provider = modalObject.data;
        if (!provider) return;

        const payload: ServiceProviderConfigUpdatePayload = {};
        const supportEmail = values.support_email.trim();
        if (supportEmail !== (provider.support_email ?? '').trim()) {
            payload.support_email = supportEmail;
        }
        if (values.support_email_password) {
            payload.support_email_password = values.support_email_password;
        }

        if (!Object.keys(payload).length) {
            onResetModalObj();
            return;
        }

        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
        try {
            const response = await api.serviceProvider.updateConfig(provider.id, payload);
            dispatch(setAlert({ message: translate('supportEmailUpdated'), type: 'success' }));
            setProviders((prev) =>
                prev.map((item) =>
                    item.id === provider.id
                        ? { ...item, support_email: response.data.support_email }
                        : item
                )
            );
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const handleFeaturesUpdated = (providerId: number, enabledFeatures: string[]) => {
        setProviders((prev) =>
            prev.map((item) =>
                item.id === providerId ? { ...item, enabled_features: enabledFeatures } : item
            )
        );
    };

    const handleCountriesUpdated = (providerId: number, enabledCountryCodes: CountryCode[]) => {
        setProviders((prev) =>
            prev.map((item) =>
                item.id === providerId
                    ? { ...item, enabled_country_codes: enabledCountryCodes }
                    : item
            )
        );
        if (providerId === currentServiceProviderId) {
            dispatch(setEnabledCountryCodes(enabledCountryCodes));
        }
    };

    return (
        <>
            <Stack spacing={2} sx={{ mb: 2 }}>
                <PageHeader title="adminPanel" titleEnd={headerControls} sx={{ mb: 0 }} />
                <AdminPanelFilters
                    companies={companyOptions}
                    selectedCompany={selectedCompany}
                    onCompanyChange={setSelectedCompany}
                    search={search}
                    onSearchChange={setSearch}
                />
            </Stack>

            <ServiceProvidersTable
                providers={filteredProviders}
                loading={isLoading}
                handleAction={handleAction}
            />

            {modalObject.action === 'FEATURES' && modalObject.data ? (
                <ServiceProviderFeaturesDialog
                    provider={modalObject.data}
                    onClose={onResetModalObj}
                    onUpdated={handleFeaturesUpdated}
                />
            ) : null}

            {modalObject.action === 'COUNTRIES' && modalObject.data ? (
                <ServiceProviderCountriesDialog
                    provider={modalObject.data}
                    onClose={onResetModalObj}
                    onUpdated={handleCountriesUpdated}
                />
            ) : null}

            {modalObject.action === 'DEFAULT_CONSULTANTS' && modalObject.data ? (
                <ServiceProviderDefaultConsultantsDialog
                    provider={modalObject.data}
                    onClose={onResetModalObj}
                />
            ) : null}

            {modalObject.action === ACTIONS.EDIT && modalObject.data ? (
                <SupportEmailForm
                    provider={modalObject.data}
                    loading={modalObject.loading || modalObject.disabled}
                    onClose={onResetModalObj}
                    onSubmit={handleSupportEmailSubmit}
                />
            ) : null}
        </>
    );
}
